import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { useSupabase } from '../../supabaseAuth';

const DeliveryModal = ({ subscription, show, onHide, onDeliveryCreated }) => {
  const [addresses, setAddresses] = useState([]);
  const [formData, setFormData] = useState({
    metal_amount: '',
    metal_cost: '',
    service_fee: '',
    tax_fee: '',
    shipping_fee: '',
    billing_address_id: '',
    shipping_address_id: '',
    delivery_notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [metalCost, setMetalCost] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const { getAccessToken } = useSupabase();

  // Fetch addresses and auto-fill form when modal opens
  useEffect(() => {
    if (show && subscription) {
      fetchAddresses();

      // Auto-fill form fields based on subscription
      const metalAmount = subscription.weight || subscription.target_weight || 0;
      const metalCostEstimate = subscription.current_balance || 0;
      const serviceFee = (metalCostEstimate * 0.10).toFixed(2); // 10% service fee
      const shippingFee = '6.99'; // Flat shipping fee
      const taxFee = '0.00'; // Tax already collected via monthly subscription payments

      setFormData({
        metal_amount: metalAmount.toString(),
        metal_cost: metalCostEstimate.toFixed(2),
        service_fee: serviceFee,
        tax_fee: taxFee,
        shipping_fee: shippingFee,
        billing_address_id: '',
        shipping_address_id: '',
        delivery_notes: ''
      });
    }
  }, [show, subscription]);

  // Auto-recalculate service fee when metal_cost changes
  useEffect(() => {
    if (formData.metal_cost) {
      const metalCostValue = parseFloat(formData.metal_cost) || 0;
      const newServiceFee = (metalCostValue * 0.10).toFixed(2);

      // Only update if it's different to avoid infinite loop
      if (newServiceFee !== formData.service_fee) {
        setFormData(prev => ({
          ...prev,
          service_fee: newServiceFee
        }));
      }
    }
  }, [formData.metal_cost]);

  // Calculate costs when form data changes
  useEffect(() => {
    calculateCosts();
  }, [formData.metal_amount, formData.metal_cost, formData.service_fee, formData.tax_fee, formData.shipping_fee]);

  const fetchAddresses = async () => {
    try {
      const token = await getAccessToken();
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';
      
      const response = await fetch(`${apiBaseUrl}/api/admin/subscriptions/${subscription.id}/addresses`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAddresses(data.addresses || []);
    } catch (err) {
      setError(`Failed to load addresses: ${err.message}`);
    }
  };

  const calculateCosts = async () => {
    try {
      const metalAmount = parseFloat(formData.metal_amount) || 0;
      const metalCost = parseFloat(formData.metal_cost) || 0;
      const serviceFee = parseFloat(formData.service_fee) || 0;
      const taxFee = parseFloat(formData.tax_fee) || 0;
      const shippingFee = parseFloat(formData.shipping_fee) || 0;

      // If metal_cost is not manually entered, calculate estimated cost
      if (!formData.metal_cost && formData.metal_amount && subscription) {
        let estimatedMetalCost = 0;
        if (subscription.metal_type === 'gold') {
          estimatedMetalCost = metalAmount * (subscription.weight_type === 'oz' ? 2000 : 64); // Rough estimate
        } else if (subscription.metal_type === 'silver') {
          estimatedMetalCost = metalAmount * (subscription.weight_type === 'oz' ? 25 : 0.8); // Rough estimate
        }
        setMetalCost(estimatedMetalCost);
        setTotalCost(estimatedMetalCost + serviceFee + taxFee + shippingFee);
      } else {
        // Use manually entered metal cost
        setMetalCost(metalCost);
        setTotalCost(metalCost + serviceFee + taxFee + shippingFee);
      }
    } catch (err) {
      console.error('Error calculating costs:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = await getAccessToken();
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';

      const response = await fetch(`${apiBaseUrl}/api/admin/subscriptions/${subscription.id}/delivery`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      onDeliveryCreated(data);
      onHide();
      
      // Reset form
      setFormData({
        metal_amount: '',
        metal_cost: '',
        service_fee: '',
        tax_fee: '',
        shipping_fee: '',
        billing_address_id: '',
        shipping_address_id: '',
        delivery_notes: ''
      });
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatAddress = (address) => {
    const parts = [];
    if (address.first_name || address.last_name) {
      parts.push(`${address.first_name || ''} ${address.last_name || ''}`.trim());
    }
    if (address.company) {
      parts.push(address.company);
    }
    parts.push(address.address_line_1);
    if (address.address_line_2) {
      parts.push(address.address_line_2);
    }
    parts.push(`${address.city}, ${address.state} ${address.zip_code}`);
    return parts.join(', ');
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-truck me-2"></i>
          Create Delivery for Subscription #{subscription?.id}
        </Modal.Title>
      </Modal.Header>
      
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

              {/* Subscription Info */}
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="card-title">Subscription Details</h6>
                  <div className="row">
                    <div className="col-md-6">
                      <p><strong>Metal Type:</strong> {subscription?.metal_type?.toUpperCase()}</p>
                      <p><strong>Weight Type:</strong> {subscription?.weight_type}</p>
                    </div>
                    <div className="col-md-6">
                      <p><strong>Current Balance:</strong> ${subscription?.current_balance?.toFixed(2)}</p>
                      <p><strong>User ID:</strong> {subscription?.user_id}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Delivery Details */}
              <div className="card mb-4">
                <div className="card-header">
                  <h6 className="mb-0">Delivery Details</h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-6 d-flex align-items-center justify-content-end">
                      <label className="form-label mb-0">Metal Amount ({subscription?.weight_type}):</label>
                    </div>
                    <div className="col-6">
                      <input
                        type="number"
                        step="0.001"
                        className="form-control"
                        value={formData.metal_amount}
                        onChange={(e) => handleInputChange('metal_amount', e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-6 d-flex align-items-center justify-content-end">
                      <label className="form-label mb-0">Metal Cost:</label>
                    </div>
                    <div className="col-6">
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={formData.metal_cost}
                        onChange={(e) => handleInputChange('metal_cost', e.target.value)}
                        placeholder={`Estimated: $${metalCost.toFixed(2)}`}
                        required
                      />
                    </div>
                    <div className="col-6 d-flex align-items-center justify-content-end">
                      <label className="form-label mb-0">Service Fee:</label>
                    </div>
                    <div className="col-6">
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={formData.service_fee}
                        onChange={(e) => handleInputChange('service_fee', e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-6 d-flex align-items-center justify-content-end">
                      <label className="form-label mb-0">Tax Fee:</label>
                    </div>
                    <div className="col-6">
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={formData.tax_fee}
                        onChange={(e) => handleInputChange('tax_fee', e.target.value)}
                        required
                      />
                      <small className="text-muted">
                        <i className="bi bi-info-circle me-1"></i>
                        Tax already collected via monthly subscription
                      </small>
                    </div>
                    <div className="col-6 d-flex align-items-center justify-content-end">
                      <label className="form-label mb-0">Shipping Fee:</label>
                    </div>
                    <div className="col-6">
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={formData.shipping_fee}
                        onChange={(e) => handleInputChange('shipping_fee', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  
                  {/* Total Only */}
                  <hr className="my-3" />
                  <div className="d-flex justify-content-between">
                    <strong>Total Cost:</strong>
                    <strong className="text-primary">${totalCost.toFixed(2)}</strong>
                  </div>
                  
                  {subscription?.current_balance && totalCost > subscription.current_balance && (
                    <div className="alert alert-danger mt-3 mb-0">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      Insufficient balance! Available: ${subscription.current_balance.toFixed(2)}
                    </div>
                  )}
                </div>
              </div>

              {/* Addresses */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Billing Address</label>
                  <select
                    className="form-select"
                    value={formData.billing_address_id}
                    onChange={(e) => handleInputChange('billing_address_id', e.target.value)}
                    required
                  >
                    <option value="">Select billing address...</option>
                    {addresses.map(address => (
                      <option key={address.id} value={address.id}>
                        {formatAddress(address)}
                        {address.is_default && ' (Default)'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Shipping Address</label>
                  <select
                    className="form-select"
                    value={formData.shipping_address_id}
                    onChange={(e) => handleInputChange('shipping_address_id', e.target.value)}
                    required
                  >
                    <option value="">Select shipping address...</option>
                    {addresses.map(address => (
                      <option key={address.id} value={address.id}>
                        {formatAddress(address)}
                        {address.is_default && ' (Default)'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Delivery Notes */}
              <div className="mb-3">
                <label className="form-label">Delivery Notes (Optional)</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.delivery_notes}
                  onChange={(e) => handleInputChange('delivery_notes', e.target.value)}
                  placeholder="Special instructions, packaging preferences, etc."
                />
              </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={onHide}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="primary" 
            size="sm" 
            disabled={loading || (subscription?.current_balance && totalCost > subscription.current_balance)}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                Create Delivery
              </>
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default DeliveryModal;